import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// CodeMirror 6 backed markdown source editor.
//
// Replaces @uiw/react-md-editor, whose hidden-textarea + visual-pre overlay
// drifts on touch devices and RTL content (caret lands away from where the
// user tapped). CodeMirror 6's contentDOM is the single source of truth for
// both rendering and selection, so the caret follows the tap and bidi text
// is positioned by the browser's own bidi engine.
//
// - Paste handler intercepts HTML clipboard payloads (e.g. from Google Docs)
//   and converts them to Markdown via Turndown + the GFM plugin. Plain text
//   falls through to CodeMirror's default insert.
// - Per-paragraph RTL via `unicode-bidi: plaintext` on `.cm-line` so
//   mixed-direction documents work without an explicit `dir` attribute.
import CodeMirror from "@uiw/react-codemirror";
import { markdown } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { EditorView } from "@codemirror/view";
import { EditorState } from "@codemirror/state";
import { githubLight, githubDark } from "@uiw/codemirror-theme-github";
import TurndownService from "turndown";
// @joplin/turndown-plugin-gfm ships as CJS with no types.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore -- no @types package
import { gfm as turndownGfm } from "@joplin/turndown-plugin-gfm";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { MarkdownEditorToolbar } from "./MarkdownEditorToolbar";
// A paste replacing more than this many characters of existing content is
// considered "large" and warrants a pre-paste checkpoint.
const LARGE_PASTE_CHARS = 500;
// Module-level Turndown — instantiating per-paste is wasteful and the
// service is stateless after configuration.
const turndownService = new TurndownService({
    headingStyle: "atx",
    codeBlockStyle: "fenced",
    bulletListMarker: "-",
    emDelimiter: "*",
    hr: "---",
});
turndownService.use(turndownGfm);
// Heuristic: most plain-text copies come through with a tiny HTML wrapper
// (Chrome wraps clipboard payloads in `<meta>...<span>...</span>`). Only
// invoke Turndown when the HTML carries real formatting — block tags,
// links/images, inline-formatting, or list markers — otherwise the
// browser's native plain-text path is faithful.
const RICH_HTML_RE = /<(?:p|h[1-6]|ul|ol|li|table|thead|tbody|tr|td|th|pre|code|blockquote|div|article|section|figure|figcaption|hr|br|strong|b|em|i|u|s|del|ins|a|img)\b/i;
function htmlLooksRich(html) {
    return RICH_HTML_RE.test(html);
}
function toPx(value) {
    if (value === undefined)
        return undefined;
    return typeof value === "number" ? `${value}px` : value;
}
export function MarkdownEditor({ value, onChange, onBlur, onLargePaste, dir, minHeight, height, autoFocus, placeholder, readOnly, colorMode = "light", hideToolbar, className, }) {
    const cmRef = useRef(null);
    const onLargePasteRef = useRef(onLargePaste);
    useEffect(() => { onLargePasteRef.current = onLargePaste; }, [onLargePaste]);
    const extensions = useMemo(() => {
        const isRtl = dir === "rtl";
        return [
            markdown({ codeLanguages: languages, addKeymap: true }),
            EditorView.lineWrapping,
            EditorView.contentAttributes.of({
                // 'auto' lets the browser apply Bidi §P2/P3 to the contentDOM as a
                // whole; the per-line `unicode-bidi: plaintext` rule below handles
                // mixed-direction paragraphs.
                dir: isRtl ? "rtl" : "auto",
            }),
            EditorView.theme({
                "&": {
                    backgroundColor: "transparent",
                    fontSize: "14.5px",
                },
                ".cm-content": {
                    fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                    padding: "12px 14px",
                    caretColor: "currentColor",
                },
                ".cm-line": {
                    unicodeBidi: "plaintext",
                    lineHeight: "1.6",
                },
                "&.cm-focused": { outline: "none" },
                ".cm-scroller": {
                    fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                    overflowAnchor: "none",
                },
                ".cm-activeLine": {
                    backgroundColor: "rgba(148, 163, 184, 0.08)",
                },
            }),
            EditorView.domEventHandlers({
                paste(event, view) {
                    const currentContent = view.state.doc.toString();
                    const html = event.clipboardData?.getData("text/html");
                    if (html && html.trim() && htmlLooksRich(html)) {
                        let md;
                        try {
                            md = turndownService.turndown(html);
                        }
                        catch {
                            return false;
                        }
                        if (!md || !md.trim())
                            return false;
                        if (md.length >= LARGE_PASTE_CHARS) {
                            onLargePasteRef.current?.(currentContent);
                        }
                        event.preventDefault();
                        view.dispatch({
                            ...view.state.replaceSelection(md),
                            scrollIntoView: true,
                            userEvent: "input.paste",
                        });
                        return true;
                    }
                    // Plain-text path: let CodeMirror handle insertion, but still fire
                    // the large-paste callback so a checkpoint can be created.
                    const text = event.clipboardData?.getData("text/plain") ?? "";
                    if (text.length >= LARGE_PASTE_CHARS) {
                        onLargePasteRef.current?.(currentContent);
                    }
                    return false;
                },
            }),
            EditorState.readOnly.of(!!readOnly),
        ];
    }, [dir, readOnly]);
    const handleChange = useCallback((next) => {
        onChange(next);
    }, [onChange]);
    return (_jsxs("div", { className: `md-editor-shell flex flex-col ${className ?? ""}`, children: [!hideToolbar && !readOnly && (_jsx(MarkdownEditorToolbar, { viewRef: cmRef })), _jsx(CodeMirror, { ref: cmRef, value: value, onChange: handleChange, onBlur: onBlur, extensions: extensions, theme: colorMode === "dark" ? githubDark : githubLight, minHeight: toPx(minHeight), height: toPx(height), autoFocus: autoFocus, placeholder: placeholder, basicSetup: {
                    lineNumbers: false,
                    foldGutter: false,
                    highlightActiveLineGutter: false,
                    autocompletion: false,
                } })] }));
}
